/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { useHistory, Prompt } from 'react-router-dom';

import { request } from '@mairistem/tools';
import {
  Button,
  Icon,
  Image,
  Segment,
  Sidebar,
  Dimmer,
  Loader,
} from '@mairistem/composants';

import { ASSISTANT_STEPS, DEFAULT_INDIVIDU, COLORS } from '@/constants';
import { type Individu, Steps } from '@/types';
import {
  validationStep1,
  validationStep2,
  validationStep3,
  validationStep4,
  validationStep5,
} from '@/utils/validations';
import { AssistantContext } from '@/contexts';
import {
  base64ToUint8Array,
  renderPdfPageToDataURL,
} from '@/utils/pdf-helpers';
import { baseUrlApp } from '@/routes';

import { useTrimestreStore } from '@/zustand';
import { useCreateIndividu, useUpdateIndividu } from '@/hooks';
import AssistantModalFin from './AssistantModalFin';
import AssistantFooter from './AssistantFooter';
import AssistantHeader from './AssistantHeader';
import { StepIdentification } from './StepIdentification';
import { StepAdressesContacts } from './StepAdressesContacts';
import { StepFiliation } from './StepFiliation';
import { StepInformationsComplementaires } from './StepInformationsComplementaires';
import { StepAttestation } from './StepAttestation';
import AssistantModalErrorStepFin from './AssistantModalErrorStepFin';
import AssistantModalErrorStep from './AssistantModalErrorStep';
import AssistantModalError from './AssistantModalError';
import ConfirmationModal from './ConfirmationModal';
import ModalEnvoieAttestation from './StepAttestation/ModalEnvoieAttestation';
import { ModalWarnTrimestreActif } from '../Common';

const Assistant = () => {
  // #region Declarations

  /** Index de l'étape actuelle */
  const [currentStepIndex, setCurrentStepIndex] = React.useState<Steps>(0);
  /** Sidebar visible */
  const [sidebarVisible, setSidebarVisible] = React.useState<boolean>(false);
  /** Modal de fin ouverte */
  const [showModalFin, setShowModalFin] = React.useState<number>();
  const [showModalError, setShowModalError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  /** Modal d'envoi attestation en mode Hubee */
  const [showModalEnvoieAttestation, setShowModalEnvoieAttestation] =
    React.useState<number>();
  /** Confirmation d'erreur sur la step, on stocke l'identifiant de la next step si validation */
  const [showModalErrorStep, setShowModalErrorStep] = React.useState<Steps>();
  /** Modal d'erreur de saisie globale avant d'envoyer au back */
  const [showModalErrorStepFin, setShowModalErrorStepFin] =
    React.useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [showModalWarnTrimestreActif, setShowModalWarnTrimestreActif] =
    React.useState(false);
  const [pendingNavigation, setPendingNavigation] = React.useState(null);
  const [navigationPath, setNavigationPath] = React.useState(null);
  // Ajout d'un état pour bloquer le Prompt pendant la navigation
  const [isNavigating, setIsNavigating] = React.useState(false);
  const [updated, setUpdated] = React.useState<boolean>(false);
  const history = useHistory();

  const individu = React.useContext(AssistantContext);
  const {
    errors,
    setErrors,
    setTeledossierData,
    onReset,
    isLoadingHubeeData,
    setIsLoadingHubeeData,
  } = individu || {};

  const isCurrentTrimestreActif = useTrimestreStore(
    (state) => state.isCurrentTrimestreActif,
  );

  // Hook pour créer un individu
  const { createIndividu } = useCreateIndividu();
  // Hook pour mettre à jour un individu brouillon
  const { updateIndividuBrouillon } = useUpdateIndividu();

  // État pour la première page du PDF 10870-PDF
  const [pdfFirstPageUrl, setPdfFirstPageUrl] = React.useState<string | null>(
    null,
  );
  const [pdfLoading, setPdfLoading] = React.useState<boolean>(false);
  // État pour savoir si on est en mode Hubee
  const [isHubeeMode, setIsHubeeMode] = React.useState<boolean>(false);
  const [identifiantTeledossier, setIdentifiantTeledossier] = React.useState<
    string | null
  >(null);
  // Identifiant de l'individu brouillon créé pour l'attestation en mode Hubee
  const [individuBrouillonId, setIndividuBrouillonId] = React.useState<
    number | null
  >(null);

  // Fonction pour extraire et afficher le PDF depuis les données
  const handlePdfExtraction = React.useCallback(
    (data: any, teledossierId?: string | null) => {
      // Cas 1: Données Hubee (piecesJointes) — Cas 2: Individu brouillon (notice)
      const notice =
        data?.piecesJointes?.find((pj: any) => pj.typeHubee === '10870-PDF') ??
        data?.notice;

      if (_.isNil(notice)) {
        return;
      }

      const displayPdf = (pdfData: Uint8Array) => {
        renderPdfPageToDataURL(pdfData, 1, 800)
          .then((dataUrl) => {
            setPdfFirstPageUrl(dataUrl);
          })
          .catch(() => {})
          .finally(() => {
            setPdfLoading(false);
          });
      };

      const pdfBinary = notice?.pieceJointeBinaire?.binary;

      if (pdfBinary) {
        setPdfLoading(true);
        try {
          displayPdf(base64ToUint8Array(pdfBinary));
        } catch (_e) {
          setPdfLoading(false);
        }
        return;
      }

      // En déployé, le binaire n'est pas sérialisé inline (cf. HubeeRepository::
      // getTeledossierById côté API) : on le récupère via l'endpoint de streaming.
      if (notice?.identifiant && teledossierId) {
        setPdfLoading(true);

        // Un seul await/then sur `request.get` : son wrapper (@mairistem/tools)
        // n'est pas un vrai thenable Promise (ses .then() s'exécutent en synchrone
        // via un reduce), donc chaîner un second traitement async dessus (comme
        // blob.arrayBuffer()) ne fonctionne pas — il faut sortir du wrapper avant.
        (async () => {
          try {
            const blob = (await request.get(
              `/api/administres/recensement-citoyen/hubee/teledossiers/${teledossierId}/documents/${notice.identifiant}/contenu`,
              {
                // Accept: '*/*' pour empêcher la gateway de tenter de convertir la réponse binaire en JSON
                headers: { Accept: '*/*' },
                responseType: 'blob',
              },
            )) as Blob;
            const buffer = await blob.arrayBuffer();
            displayPdf(new Uint8Array(buffer));
          } catch (_e) {
            setPdfLoading(false);
          }
        })();
      }
    },
    [],
  );

  // Vérifie d'abord si un brouillon existe pour ce télédossier
  React.useEffect(() => {
    // Récupérer l'identifiant du télédossier depuis les paramètres de l'URL
    const { search } = history?.location || {};
    const teledossierId = search?.split('identifiantTeledossier=')[1];
    setIsHubeeMode(!!teledossierId);
    setIdentifiantTeledossier(teledossierId || null);

    if (teledossierId) {
      if (setIsLoadingHubeeData) {
        setIsLoadingHubeeData(true);
      }

      // La route retourne soit l'individu brouillon s'il existe,
      // soit les données du télédossier Hubee sinon
      request
        .get(
          `/api/administres/recensement-citoyen/individus/teledossiers/${teledossierId}`,
        )
        .then((response: any) => {
          // Détecter si c'est un individu brouillon (a un identifiant et isBrouillon pas null)
          // ou des données Hubee (a demandeur structuré)
          const isExistingBrouillon =
            response?.identifiant && !_.isNil(response?.isBrouillon);

          if (isExistingBrouillon) {
            // C'est un individu brouillon existant
            setIndividuBrouillonId(response.identifiant);

            // Passer les données du brouillon au provider
            if (setTeledossierData) {
              setTeledossierData({
                isExistingBrouillon: true,
                individu: response,
              });
            }

            handlePdfExtraction(response, teledossierId);

            if (setIsLoadingHubeeData) {
              setIsLoadingHubeeData(false);
            }
          } else {
            // C'est un nouveau télédossier au format Hubee
            if (setTeledossierData) {
              setTeledossierData(response);
            }
            handlePdfExtraction(response, teledossierId);
          }
        })
        .catch(() => {
          if (setIsLoadingHubeeData) {
            setIsLoadingHubeeData(false);
          }
        });
    }
  }, [
    setTeledossierData,
    setIsLoadingHubeeData,
    history.location,
    handlePdfExtraction,
  ]);

  // Effet pour gérer la navigation
  React.useEffect(() => {
    if (navigationPath && isNavigating) {
      history.push(navigationPath);
      setNavigationPath(null);
    }
  }, [navigationPath, isNavigating, history]);

  React.useEffect(() => {
    if (
      !_.isEqual(
        _.omit(individu, [
          'errors',
          'setErrors',
          'onChange',
          'anneeRecensement',
          'trimestreRecensement',
          'dateRecensement',
          'adresseContact',
          'onReset',
        ]),
        _.omit(DEFAULT_INDIVIDU, [
          'anneeRecensement',
          'trimestreRecensement',
          'dateRecensement',
          'adresseContact',
        ]),
      )
    ) {
      setUpdated(true);
    } else {
      setUpdated(false);
    }
  }, [individu]);

  React.useEffect(() => {
    if (!isCurrentTrimestreActif) {
      setShowModalWarnTrimestreActif(true);
    }
  }, [isCurrentTrimestreActif]);

  // #region Functions

  const handleErrors = React.useCallback(
    (current: Steps, stepIndex: Steps) => {
      let hasErrors = false;
      const newErrors = {};

      // On vérifie toutes les étapes de currentStepIndex jusqu'à stepIndex
      for (let step = current; step <= stepIndex - 1; step++) {
        let stepErrors = null;
        switch (step) {
          case Steps.Identification:
            stepErrors = validationStep1(individu);
            break;
          case Steps.AdressesContacts:
            stepErrors = validationStep2(individu);
            break;
          case Steps.Filiation:
            stepErrors = validationStep3(individu);
            break;
          case Steps.InformationsComplementaires:
            stepErrors = validationStep4(individu);
            break;
          case Steps.Attestation:
            stepErrors = validationStep5(individu);
            break;
          default:
            break;
        }

        if (!_.isEmpty(stepErrors)) {
          hasErrors = true;
        }
        newErrors[step] = stepErrors;
      }

      setErrors((prev) => ({
        ...prev,
        ...newErrors,
      }));

      return hasErrors;
    },
    [individu, setErrors],
  );

  /**
   * Callback appelé lors du changement de step
   */
  const handleStepChange = React.useCallback(
    (stepIndex: Steps) => {
      // Si on recule, on change directement d'étape sans validation
      if (stepIndex < currentStepIndex) {
        setCurrentStepIndex(stepIndex);
        return;
      }

      // En mode Hubee, si on essaye d'aller à la partie 5 (Attestation)
      if (isHubeeMode && stepIndex === Steps.Attestation) {
        // Vérifier que les 4 premières étapes sont valides
        const hasErrors = handleErrors(Steps.Identification, Steps.Attestation);

        if (hasErrors) {
          // Bloquer la navigation
          return;
        }

        // Si tout est valide et qu'on n'a pas encore créé de brouillon
        if (!individuBrouillonId) {
          setLoading(true);

          // Créer directement un nouveau brouillon
          // (la vérification de l'existence d'un brouillon a déjà été faite au chargement initial)
          createIndividu(individu, true, identifiantTeledossier)
            .then((newIndividu: Individu) => {
              const newIdentifiant = newIndividu?.identifiant as number;
              if (!newIdentifiant) {
                setShowModalError(true);
                return;
              }
              setIndividuBrouillonId(newIdentifiant);
              setCurrentStepIndex(stepIndex);
            })
            .catch(() => {
              setShowModalError(true);
            })
            .finally(() => {
              setLoading(false);
            });
        } else {
          // Si on a déjà un brouillon, on le met à jour avec les nouvelles données
          setLoading(true);
          updateIndividuBrouillon(individuBrouillonId, individu)
            .then(() => {
              setCurrentStepIndex(stepIndex);
            })
            .catch(() => {
              setShowModalError(true);
            })
            .finally(() => {
              setLoading(false);
            });
        }
        return;
      }

      const hasErrors = handleErrors(currentStepIndex, stepIndex);

      // Si on a des erreurs on affiche la modal de confirmation
      if (hasErrors) {
        setShowModalErrorStep(stepIndex);
      } else {
        setCurrentStepIndex(stepIndex);
      }
    },
    [
      currentStepIndex,
      handleErrors,
      isHubeeMode,
      individuBrouillonId,
      createIndividu,
      updateIndividuBrouillon,
      individu,
      identifiantTeledossier,
    ],
  );

  const handleValidateModalErrorStep = React.useCallback(() => {
    setCurrentStepIndex(showModalErrorStep as Steps);
    setShowModalErrorStep(null);
  }, [showModalErrorStep]);

  const handleValidateModalErrorStepFin = React.useCallback(() => {
    // On trouve le premier index qui a des erreurs non vides
    const stepToGo = Object.keys(errors).find((key) => !_.isEmpty(errors[key]));
    const stepToDisplay = parseInt(stepToGo as string, 10) as Steps;
    setCurrentStepIndex(stepToDisplay);
    setShowModalErrorStepFin(false);
  }, [errors]);

  const handleValidate = React.useCallback(() => {
    setLoading(true);

    createIndividu(individu)
      .then((newIndividu: Individu) => {
        setShowModalFin(newIndividu?.identifiant as number);
      })
      .catch(() => {
        setShowModalError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [individu, createIndividu]);
  /**
   * Callback appelé lors du clic sur le bouton "Terminer"
   */
  const handleFinish = React.useCallback(async () => {
    const hasErrors = handleErrors(Steps.Identification, Steps.Attestation + 1);

    if (!hasErrors) {
      // En mode Hubee, on ouvre directement la modal d'envoi d'attestation
      // La création de l'individu se fera dans la modal
      if (isHubeeMode) {
        setShowModalEnvoieAttestation(-1); // -1 pour indiquer qu'on n'a pas encore d'identifiant
      } else {
        handleValidate();
      }
    } else {
      setShowModalErrorStepFin(true);
    }
  }, [handleErrors, handleValidate, isHubeeMode]);

  const handleBlockNavigation = React.useCallback(
    (location) => {
      if (loading || isNavigating || !updated) return false;

      setPendingNavigation(location);
      setShowConfirmModal(true);
      return false;
    },
    [loading, isNavigating, updated],
  );

  const handleConfirmNavigation = React.useCallback(() => {
    if (pendingNavigation) {
      setNavigationPath(pendingNavigation.pathname);
      setIsNavigating(true);
      setPendingNavigation(null);
      setShowConfirmModal(false);
    }
  }, [pendingNavigation]);

  const handleToggleSidebar = React.useCallback(() => {
    setSidebarVisible((prev) => !prev);
  }, []);

  /**
   * Callback appelé à la fin du processus de la modal d'envoi d'attestation
   * (que l'utilisateur clique sur Oui ou Non)
   */
  const handleModalEnvoieAttestation = React.useCallback(() => {
    // On ferme la modal et on redirige vers l'accueil ou la liste
    setShowModalEnvoieAttestation(undefined);
    history.push(baseUrlApp);
  }, [history]);

  const handleResetAssistant = React.useCallback(() => {
    setIsNavigating(false);
    setUpdated(false);
    setCurrentStepIndex(0);
    setShowModalErrorStep(null);
    setShowModalErrorStepFin(false);
    setPendingNavigation(null);
    setNavigationPath(null);
    setLoading(false);
    setShowModalFin(null);
    setIndividuBrouillonId(null);
    onReset();
  }, [onReset]);

  // #endregion

  // #region Components

  const stepToDisplay = React.useMemo(() => {
    switch (currentStepIndex) {
      case Steps.Identification:
        return <StepIdentification />;

      case Steps.AdressesContacts:
        return <StepAdressesContacts />;

      case Steps.Filiation:
        return <StepFiliation />;

      case Steps.InformationsComplementaires:
        return <StepInformationsComplementaires />;

      case Steps.Attestation:
        return isHubeeMode ? (
          <StepAttestation identifiant={individuBrouillonId} />
        ) : null;

      default:
        return <h1>Une erreur est survenue...</h1>;
    }
  }, [currentStepIndex, isHubeeMode, individuBrouillonId]);

  return (
    <>
      <Prompt
        when={
          !loading &&
          !isNavigating &&
          updated &&
          _.isNil(showModalFin) &&
          _.isNil(showModalEnvoieAttestation)
        }
        message={handleBlockNavigation}
      />
      <Sidebar.Pushable
        style={{ height: '100%', overflow: 'hidden', position: 'relative' }}
      >
        {isHubeeMode && (
          <>
            <Button
              icon={sidebarVisible}
              onClick={handleToggleSidebar}
              style={{
                position: 'fixed',
                margin: 0,
                right: 0,
                top: 'calc(50% - 20px)',
                zIndex: 1001,
                borderTopLeftRadius: '50px',
                borderBottomLeftRadius: '50px',
                height: '70px',
                width: '80px',
                alignItems: 'center',
                justifyItems: 'center',
                backgroundColor: COLORS.Background,
                color: 'white',
                cursor: 'pointer',
                transform: sidebarVisible
                  ? 'translateX(-34vw)'
                  : 'translateX(0)',
                transition: 'transform 0.4s linear',
                padding: sidebarVisible ? undefined : '0',
              }}
            >
              {sidebarVisible ? (
                <Icon name="angle right" />
              ) : (
                <Image
                  className="sidebar-icon"
                  style={{ width: '40px', height: '40px' }}
                />
              )}
            </Button>

            <Sidebar
              animation="overlay"
              direction="right"
              visible={sidebarVisible}
              width="very wide"
              style={{
                backgroundColor: 'white',
                width: '34vw',
                padding: '1em',
                transitionBehavior: 'normal, normal',
                transitionDuration: '0.4s, 0.4s',
                transitionTimingFunction: 'linear, linear',
                transitionDelay: '0s, 0s',
                transitionProperty: 'transform, box-shadow',
              }}
            >
              {pdfLoading ? (
                <div style={{ textAlign: 'center', padding: '2em' }}>
                  <p>Chargement du PDF...</p>
                </div>
              ) : (
                <Image
                  src={pdfFirstPageUrl}
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                />
              )}
            </Sidebar>
          </>
        )}

        <Sidebar.Pusher>
          <div
            style={{
              height: '100%',
              padding: '1em',
            }}
          >
            <Segment
              raised
              style={{
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: 'white',
                borderRadius: '16px',
              }}
            >
              {isHubeeMode && isLoadingHubeeData && (
                <Dimmer active inverted>
                  <Loader size="large">
                    Chargement des données du demandeur...
                  </Loader>
                </Dimmer>
              )}
              <AssistantHeader
                currentStepIndex={currentStepIndex}
                onStepChange={handleStepChange}
                steps={
                  isHubeeMode
                    ? ASSISTANT_STEPS
                    : _.omit(ASSISTANT_STEPS, [Steps.Attestation])
                }
                isHubeeMode={isHubeeMode}
              />
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  overflowY: 'auto',
                  padding: '1em 1em',
                }}
              >
                {stepToDisplay}
              </div>
              <AssistantFooter
                currentStepIndex={currentStepIndex}
                onStepChange={handleStepChange}
                onFinish={handleFinish}
                nbSteps={
                  isHubeeMode
                    ? _.size(ASSISTANT_STEPS) - 1
                    : _.size(ASSISTANT_STEPS) - 2
                }
                loading={loading}
              />
            </Segment>
          </div>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
      {!_.isNil(showModalFin) && (
        <AssistantModalFin
          identifiant={showModalFin}
          nomFamille={individu?.nomFamille}
          prenom={individu?.prenom}
          onClose={() => setShowModalFin(null)}
          onReset={handleResetAssistant}
        />
      )}
      {showModalError && (
        <AssistantModalError
          isOpen={showModalError}
          onClose={() => setShowModalError(false)}
        />
      )}
      {showModalErrorStep && (
        <AssistantModalErrorStep
          onClose={() => setShowModalErrorStep(null)}
          onValidate={handleValidateModalErrorStep}
        />
      )}
      {showModalErrorStepFin && (
        <AssistantModalErrorStepFin
          onClose={() => setShowModalErrorStepFin(false)}
          onValidate={handleValidateModalErrorStepFin}
        />
      )}
      {showConfirmModal && (
        <ConfirmationModal
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmNavigation}
        />
      )}
      {!_.isNil(showModalEnvoieAttestation) && (
        <ModalEnvoieAttestation
          open={!_.isNil(showModalEnvoieAttestation)}
          onClose={handleModalEnvoieAttestation}
          onCancel={() => setShowModalEnvoieAttestation(null)}
          onOpen={() => {}}
          nomRecense={
            individu?.prenom && individu?.nomFamille
              ? `${individu.nomFamille} ${individu.prenom}`
              : ''
          }
          identifiantTeledossier={identifiantTeledossier}
          individu={individu}
          individuBrouillonId={individuBrouillonId}
          onError={() => setShowModalError(true)}
        />
      )}
      {showModalWarnTrimestreActif && (
        <ModalWarnTrimestreActif
          onClose={() => setShowModalWarnTrimestreActif(false)}
          assistant
        />
      )}
    </>
  );
};

export default Assistant;
